<!--
	@component

	Registration for one course term, opened as a side panel over the course detail – the same
	drawer pattern the cart and the subscription configurator use.

	Submitting puts the term into the cart and continues to the checkout, exactly like the inline
	form did before.
-->
<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Link from "@components/common/Link.svelte";
	import SideModal from "@components/common/SideModal.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublicCourseTerm } from "@core/schema/CourseTerm.js";
	import { courseTypeNameMap } from "@core/schema/names/courseTypeNameMap.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getTermsOfServicePath } from "@core/schema/paths/getTermsOfServicePath.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { formatPhoneNumberOnInput } from "@core/utils/svelte/formatPhoneNumberOnInput.js";
	import { toDateTimeMonthYearFormat } from "@core/utils/toDateTimeMonthYearFormat.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./CourseRegistrationModal.translations.json" with { type: "json" };

	let {
		show = $bindable(false),
		/* eslint-disable prefer-const */
		term,
		price,
		/* eslint-enable prefer-const */
	}: { show?: boolean; term: PublicCourseTerm; price: string } = $props();

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const isDesktop = createReactiveBreakpoint(">2xl");

	let firstName = $state(Cart.state?.order.billingAddress?.firstName ?? "");
	let lastName = $state(Cart.state?.order.billingAddress?.lastName ?? "");
	let email = $state(Cart.state?.order.email ?? "");
	let phone = $state(Cart.state?.order.phoneNumber ?? "");
	let submitting = $state(false);
	let failed = $state(false);

	const alreadyInCart = $derived(Boolean(Cart.state?.hasCourseOrder && Cart.state.courseOrder.term?.id === term.id));

	function submit(): void {
		if (!Cart.loaded) {
			failed = true;
			return;
		}
		submitting = true;
		failed = false;
		const now = new Date().toISOString();
		Cart.saveOrderFields({
			email,
			phoneNumber: phone,
			billingAddress: { firstName, lastName },
			...(alreadyInCart ? {} : { gdprApprovedAt: now, termsAndConditionsApprovedAt: now }),
		});
		if (Cart.addCourseOrder(term)) {
			// Keep the button in its loading state, the browser is leaving the page.
			location.assign(getCartPath(tenant));
			return;
		}
		// The cart holds something that cannot be combined with a course – it asks what to do next.
		submitting = false;
		show = false;
	}
</script>

<SideModal bind:show fullContentWidth heading={translate("heading")}>
	<Form class="flex flex-col grow shrink min-h-0" onsubmit={submit}>
		<div class="grow shrink px-4 md:px-12 overflow-y-auto overscroll-contain flex flex-col gap-6 pb-10 pt-2 md:pt-4">
			<div class="flex flex-col gap-1 rounded-2xl bg-fcrlightgray-400 p-4 md:p-6">
				<div class="font-antipol font-medium text-base lg:text-1.5xl/8 text-fcrdarkgray">
					<Formatted text={courseTypeNameMap[term.courseType][tenant.locale]} />
				</div>
				<div class="text-sm/normal md:text-base font-medium">
					<Formatted text={toDateTimeMonthYearFormat(term.startsAt, tenant.locale)} />
				</div>
				<div class="text-sm/normal md:text-base text-fcrgray">
					<Formatted text="{translate('placeHeading')}:" />
					<Link href={term.placeUrl} target="_blank" rel="noopener noreferrer" class="underline">
						<Formatted text={term.placeName} />
					</Link>
				</div>
			</div>

			<div class="flex flex-col gap-2">
				<Input
					required
					placeholder={translate("firstName")}
					autocomplete="given-name"
					name="firstName"
					bind:value={firstName}
				/>
				<Input
					required
					placeholder={translate("lastName")}
					autocomplete="family-name"
					name="lastName"
					bind:value={lastName}
				/>
				<Input
					required
					placeholder={translate("email")}
					autocomplete="email"
					type="email"
					name="email"
					bind:value={email}
				/>
				<Input
					required
					placeholder={translate("phone")}
					name="phone"
					autocomplete="tel"
					type="tel"
					bind:value={phone}
					oninput={formatPhoneNumberOnInput}
					maxlength={20}
				/>
			</div>

			{#if !alreadyInCart}
				<div class="flex flex-col gap-4 font-medium">
					<LabelledInput type="checkbox" name="gdpr" required>
						<Formatted text={translate("agreeWithGdpr", { url: getGdprPath(tenant) })} />
					</LabelledInput>
					<LabelledInput type="checkbox" name="terms" required>
						<Formatted text={translate("agreeWithTerms", { url: getTermsOfServicePath(tenant) })} />
					</LabelledInput>
				</div>
			{/if}

			{#if failed}
				<p class="text-fcrerror text-sm/normal" role="alert">
					<Formatted text={translate("error")} />
				</p>
			{/if}
		</div>

		<div
			class={[
				"grow-0 shrink-0 bg-fcrbeige px-4 md:px-12 w-full z-10 bottom-0 sticky md:static",
				"pb-[max(env(safe-area-inset-bottom,0),0.5rem)] xl:pb-4 2xl:pb-6",
			]}
		>
			<div class="flex justify-between items-center text-fcrdarkgray font-medium py-4 2xl:py-6">
				<div class="text-4"><Formatted text={translate("totalWithVat")} /></div>
				<div class="text-5.5"><Formatted text={price} /></div>
			</div>
			<Button
				type="submit"
				variant={ButtonVariant.Dark}
				size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
				loading={submitting}
				class="w-full"
			>
				<Formatted text={translate(alreadyInCart ? "editSubmit" : "submit")} />
			</Button>
		</div>
	</Form>
</SideModal>
